import { useEffect, useRef, useState } from 'react';

const TRAILING_SECONDS = 0.35;
const SLOWEST_CHARS_PER_SECOND = 90;
const FASTEST_CHARS_PER_SECOND = 1500;

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
}

function endOfWord(text: string, index: number): number {
  if (index >= text.length) {
    return text.length;
  }
  const space = text.slice(index).search(/\s/);
  return space < 0 ? text.length : index + space;
}

export function useSmoothReveal(text: string, streaming: boolean): string {
  const paced = streaming && !prefersReducedMotion();
  const [shownLength, setShownLength] = useState(paced ? 0 : text.length);
  const shownRef = useRef(shownLength);

  useEffect(() => {
    if (!paced) {
      return undefined;
    }
    let frame = 0;
    let last = performance.now();
    const step = (now: number) => {
      const backlog = text.length - shownRef.current;
      if (backlog <= 0) {
        return;
      }
      const charsPerSecond = Math.min(
        FASTEST_CHARS_PER_SECOND,
        Math.max(SLOWEST_CHARS_PER_SECOND, backlog / TRAILING_SECONDS),
      );
      const advance = Math.max(1, Math.round((charsPerSecond * (now - last)) / 1000));
      last = now;
      shownRef.current = endOfWord(text, shownRef.current + advance);
      setShownLength(shownRef.current);
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [paced, text]);

  return paced ? text.slice(0, shownLength) : text;
}
